import React, { useState } from "react";
import { Navigate } from "react-router-dom";

import { faPlus } from "@fortawesome/pro-regular-svg-icons";

import { ButtonStandard, Pagination } from "@performyard/py-components";

import PageHeading from "../../../components/PageHeading";
import AllWorkflowsTable from "./AllWorkflowsTable";
import ReadableWorkflowsFilterChips from "./ReadableWorkflowsFilterChips";
import WorkflowsFilterPanel, { WorkflowsFilters } from "./WorkflowsFilterPanel";

import type { SortDirection } from "../../../api/hooks";
import { useWorkflows } from "../../../api/hooks";
import { useIsNewNavigation } from "../../../api/hooks/defs/navigation";
import useNavigate from "../../../hooks/useNavigate";

import type { WorkflowSortBy } from "../../../api/services/defs/workflows";

import { appendCurrentLocationAsFrom } from "../../../utils/workflowsBreadcrumbs";

import InfoPageTemplate from "../../../pageTemplates/InfoPageTemplate";
import StandardToolbarActionRow from "../../../pageTemplates/InfoPageTemplate/StandardToolbarActionRow";
import useInfoPageTemplateQueryState from "../../../pageTemplates/InfoPageTemplate/useInfoPageTemplateQueryState";
import WorkflowsInfoPanel from "../WorkflowsInfoPanel";
import { WORKFLOWS_NAVIGATION_LINKS } from "../navigationLinks";
import { isNamedReference, sanitizePageNumber } from "../todoFilterState";
import useWorkflowsPageGate from "../useWorkflowsPageGate";

const PAGE_TITLE = "Company Workflows";
const PAGE_SIZE = 25;

// The sort params live in the URL, so they can hold arbitrary values (old bookmarks
// from the legacy page used `name`/`direction` for filters). Fall back to the
// defaults instead of sending an invalid sort the API would reject.
const sanitizeWorkflowSortBy = (value: string): WorkflowSortBy => (value === "name" ? "name" : "start_date");
const sanitizeWorkflowSortDirection = (value: string): SortDirection => (value === "asc" ? "asc" : "desc");

const MAX_NAME_FILTER_LENGTH = 256;

const normalizeWorkflowsFilters = (filters: Partial<WorkflowsFilters>): Partial<WorkflowsFilters> => {
	const normalized: Partial<WorkflowsFilters> = {};
	if (typeof filters.workflowName === "string" && filters.workflowName) {
		normalized.workflowName = filters.workflowName.slice(0, MAX_NAME_FILTER_LENGTH);
	}
	if (isNamedReference(filters.subject)) {
		normalized.subject = { id: filters.subject.id, name: filters.subject.name };
	}
	if (filters.status === "active" || filters.status === "completed" || filters.status === "overdue") {
		normalized.status = filters.status;
	}
	if (Array.isArray(filters.overdueOnly) && filters.overdueOnly.length > 0) {
		normalized.overdueOnly = ["true"];
	}
	if (Array.isArray(filters.unassignedOnly) && filters.unassignedOnly.length > 0) {
		normalized.unassignedOnly = ["true"];
	}
	return normalized;
};

const AllWorkflows = () => {
	const isNewNav = useIsNewNavigation();
	const navigate = useNavigate();
	const [isFiltersOpen, setIsFiltersOpen] = useState(false);

	const { isGateLoading, featureAvailable, canView, canManage } = useWorkflowsPageGate();

	const {
		pageNumber,
		setPageNumber,
		sort,
		setSort,
		filters,
		setFilters,
		searchValue,
		debouncedSearchValue,
		setDebouncedSearch,
	} = useInfoPageTemplateQueryState<WorkflowsFilters>({
		defaultSortName: "start_date",
		defaultSortDirection: "desc",
		defaultPageNumber: 1,
		normalizeFilters: normalizeWorkflowsFilters,
	});
	const safePageNumber = sanitizePageNumber(pageNumber);

	const { data, isLoading } = useWorkflows(
		{
			sortBy: sanitizeWorkflowSortBy(sort.name),
			sortOrder: sanitizeWorkflowSortDirection(sort.direction),
			limit: PAGE_SIZE,
			skip: (safePageNumber - 1) * PAGE_SIZE,
			name: debouncedSearchValue?.slice(0, MAX_NAME_FILTER_LENGTH) || undefined,
			workflowName: filters.workflowName || undefined,
			subject: filters.subject?.id,
			status: filters.status && filters.status !== "any" ? filters.status : undefined,
			overdueOnly: (filters.overdueOnly?.length ?? 0) > 0 || undefined,
			unassignedOnly: (filters.unassignedOnly?.length ?? 0) > 0 || undefined,
		},
		{ enabled: !isGateLoading && featureAvailable && canView }
	);

	if (isGateLoading) {
		return null;
	}

	if (!featureAvailable) {
		return <Navigate to="/feature-disabled/" replace />;
	}

	if (!canView) {
		return <WorkflowsInfoPanel>Missing required permissions.</WorkflowsInfoPanel>;
	}

	const workflows = data?.workflows ?? [];
	const totalCount = data?.totalCount ?? 0;
	const pageCount = Math.ceil(totalCount / PAGE_SIZE);

	const hasActiveFilters = Object.keys(filters).length > 0 || !!debouncedSearchValue;

	const actionButtons = canManage
		? [
				<ButtonStandard
					key="launch-workflow"
					data-testid="launchWorkflowButton"
					color="primary"
					iconLeft={faPlus}
					dangerouslySetClassName="w-auto"
					onClick={() => navigate(appendCurrentLocationAsFrom("/launch-workflow/"))}
				>
					Launch Workflow
				</ButtonStandard>,
			]
		: [];

	const toolbarActionRow = (
		<StandardToolbarActionRow
			searchInput={searchValue}
			onSearchInputChange={setDebouncedSearch}
			onFilterButtonPress={() => setIsFiltersOpen(!isFiltersOpen)}
			activeFiltersCount={Object.keys(filters).length}
			searchPlaceholder="Search Workflows..."
		/>
	);

	const toolbarInformationRow = <ReadableWorkflowsFilterChips filters={filters} setFilters={setFilters} />;

	const drawer = (
		<WorkflowsFilterPanel
			isOpen={isFiltersOpen}
			onClose={() => setIsFiltersOpen(false)}
			filters={filters}
			onApplyFilters={(newFilters) => setFilters(newFilters)}
		/>
	);

	const mainContent = (
		<div>
			<AllWorkflowsTable
				workflows={workflows}
				isLoading={isLoading}
				onSortChange={setSort}
				activeSort={sanitizeWorkflowSortBy(sort.name)}
				activeSortDirection={sanitizeWorkflowSortDirection(sort.direction)}
				emptyStateText={hasActiveFilters ? "No workflows match the applied filters" : "No Workflows Found"}
			/>
			{pageCount > 1 && (
				<div data-testid="paginationContainer" className="mt-4 flex justify-center">
					<Pagination currentPage={safePageNumber} pageCount={pageCount} onPageChange={setPageNumber} />
				</div>
			)}
		</div>
	);

	return (
		<InfoPageTemplate
			actionButtons={actionButtons}
			toolbarActionRow={toolbarActionRow}
			toolbarInformationRow={toolbarInformationRow}
			drawer={drawer}
			mainContent={mainContent}
			pageTitle={PAGE_TITLE}
			pageHeading={isNewNav ? <PageHeading>{PAGE_TITLE}</PageHeading> : undefined}
			navigationLinks={WORKFLOWS_NAVIGATION_LINKS}
			activeNavigationLinkKey="all-workflows"
		/>
	);
};

export default AllWorkflows;
